import React, { useEffect, useRef, useState } from "react";
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Box,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import OpenTaskIcon from "../../assets/common/Open-task-icon.svg";
import CloseTaskIcon from "../../assets/common/Close-task-icon.svg";
import "../../styles/actionItems.css";
import ActionItemPlusIcon from "../../assets/action-items/Action-item-plus-icon.svg";
import { DateRangeValue } from "../../utils/types";

interface GroupByTaskItem {
  title: React.ReactNode;
  body: React.ReactNode;
  id: string;
}

interface GroupByTaskProps {
  items: GroupByTaskItem[];
  loading?: boolean;
  onExpand?: (id: string | null) => void;
  setOpenDialog?: React.Dispatch<React.SetStateAction<boolean>>
  groupByValue?: string
  groupByList?: {
    interactions: any[];
    clients: any[];
    assignees: any[];
    recurringInteractions: any[];
  };
  setGroupByValues?: React.Dispatch<
    React.SetStateAction<{
      groupByInteractionId: string | null;
      groupByClientDomain: { clientName: string | null; orgId: string | null } | null;
      groupByAssigneeId: string | null;
      groupByStatus: string | null;
    }>
  >;
  selectedGroupType?: string;
  setIsPreview?: React.Dispatch<React.SetStateAction<boolean>>
  setSelectedActionItem?: React.Dispatch<any>
  setIsNewActionItem?: React.Dispatch<React.SetStateAction<boolean>>
  columnFilters?: any
  dateRange?: DateRangeValue;
  selectedTab?: string;
  onAdd?: (id: string | null) => void;
  groupByRecurring?: boolean;
  mainScrollRef?: React.RefObject<HTMLDivElement>;
  isCompleteMomTask?: boolean;
  isAiRecommendedTasks?: boolean;
}

  const GroupByTask = React.forwardRef<any, GroupByTaskProps>(({
  items,
  loading,
  onExpand,
  setOpenDialog,
  groupByValue,
  groupByList,
  setGroupByValues,
  selectedGroupType,
  setIsPreview,
  setSelectedActionItem,
  setIsNewActionItem,
  columnFilters,
  dateRange,
  selectedTab,
  onAdd,
  groupByRecurring,
  mainScrollRef,
  isCompleteMomTask,
  isAiRecommendedTasks,
}, ref) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const summaryRefs = useRef<(HTMLDivElement | null)[]>([]);
  React.useImperativeHandle(ref, () => ({
    scrollToExpanded: (index: number) => {
      const el = summaryRefs.current[index];
      const container = mainScrollRef?.current;
  
      if (!el || !container) return;
  
      const scrollOffset =
        el.getBoundingClientRect().top -
        container.getBoundingClientRect().top +
        container.scrollTop - 10;
  
      container.scrollTo({
        top: scrollOffset,
        behavior: "smooth",
      });
    }
  }));
  

    const applyGroupByValues = (index: number) => {
      if (!setGroupByValues || !groupByList || !selectedGroupType) return;
    
      const selectedId = items[index].id;
      let newValues = {
        groupByInteractionId: null as string | null,
        groupByClientDomain: null as { clientName: string | null; orgId: string | null } | null,
        groupByAssigneeId: null,
        groupByStatus: null as string | null,
      };
    
      if (selectedGroupType === "interaction") {
        const matched = groupByList.interactions.find(
          (item) => item.interactionId === selectedId
        );
        if (matched) newValues.groupByInteractionId = matched.interactionId;
      } else if (selectedGroupType === "client") {
        const matched = groupByList.clients.find(
          (item) => item.orgId === selectedId
        );
        if (!matched || matched.orgId == null) {
          newValues.groupByClientDomain = {
            clientName: "",
            orgId: null,
          };
        }
        else if (matched.name === "Not Client Tasks") {
          newValues.groupByClientDomain = {
            clientName: "",
            orgId: matched.orgId ?? null,
          };
        }
        else {
          newValues.groupByClientDomain = {
            clientName: matched.name ?? "",
            orgId: matched.orgId ?? null,
          };
        }
      } else if (selectedGroupType === "assignee") {
        if (selectedId === "UNASSIGNED") {
          newValues.groupByAssigneeId = null;
        } else {
          const matched = groupByList.assignees.find(
            (item) => item.profileOrgId === selectedId
          );
          if (matched) {
            newValues.groupByAssigneeId = matched.profileOrgId;
          }
        }
      } else if (selectedGroupType === "status") {
        newValues.groupByStatus = selectedId;
      }
    
      setGroupByValues(newValues);
    };  
  const handleChange =
    (id: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedId(isExpanded ? id : null);
      onExpand?.(isExpanded ? id : null);

      if (!setGroupByValues || !groupByList || !selectedGroupType) return;

      if (!isExpanded) {
        setGroupByValues({
          groupByInteractionId: null,
          groupByClientDomain: null,
          groupByAssigneeId: null,
          groupByStatus: null,
        });
        return;
      }

      const index = items.findIndex((i) => i.id === id);
      if (index !== -1) {
        applyGroupByValues(index);
      }
    };
    const handleAddAction = (event: React.MouseEvent, id: string) => {
      event.stopPropagation();
      if (expandedId !== id) {
        setExpandedId(id);
        onExpand?.(id);
      }
      onAdd?.(id);
      const index = items.findIndex((i) => i.id === id);
      if (index !== -1) {
        applyGroupByValues(index);
      }
      setIsPreview?.(false);
      setSelectedActionItem?.(null);
      setOpenDialog?.(true);
      setIsNewActionItem?.(true);
    };    
    useEffect(() => {
      if(groupByValue){
        setExpandedId(null);
      }
    }, [groupByValue]);

    useEffect(() => {
      setExpandedId(null);
    }, [columnFilters, dateRange, selectedTab]);    
  if (loading) {
    return (
      <Box
        className="group-by-task-loading"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
        }}
      >
        <CircularProgress size={40} thickness={4} />
      </Box>
    );
  }

  if (!items || items.length === 0) {
    return (
      <Box
        className="group-by-task-empty"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
          color: "#888",
        }}
      >
        No tasks available.
      </Box>
    );
  }
  return (
    <Box className="group-by-task-container">
      {items.map((item, index) => (
        <Accordion
          key={item.id}
          disableGutters
          elevation={0}
          square
          expanded={expandedId === item.id}
          onChange={()=>{}}
          className="group-by-task-accordion"
        >
          <AccordionSummary
            className="group-by-task-summary"
            ref={(el) => (summaryRefs.current[index] = el)}
            expandIcon={
              <Box
                component="img"
                src={expandedId === item.id ? CloseTaskIcon : OpenTaskIcon}
                alt={expandedId === item.id? "Collapse" : "Expand"}
                className="group-by-task-icon"
                onClick={(e) => {
                  e.stopPropagation();      // prevent summary click
                  handleChange(item.id)(e, expandedId !== item.id);
                }}
              />
            }
          >
            <Box className="group-by-task-title-wrapper">
              <Box
                className="group-by-task-title base-text"
                onClick={(e) => {
                  e.stopPropagation();
                  handleChange(item.id)(e, expandedId !== item.id);
                }}
              >
                {item.title}
              </Box>
              {groupByRecurring && !isCompleteMomTask && !isAiRecommendedTasks && (
              <Tooltip title= "Add Task" placement="top" arrow>
              <Box
                component="img"
                src={ActionItemPlusIcon}
                alt="Add"
                className="group-by-task-plus-icon"
                onClick={(e) => handleAddAction(e, item.id)}
                />
                </Tooltip>
              )}
            </Box>
          </AccordionSummary>
          <AccordionDetails className="group-by-task-details">
            {expandedId === item.id && item.body}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
});

export default GroupByTask;
